/**
 * Contrast helpers for customer brand colours.
 *
 * Every surface paints with whatever colours a business uploaded, which means
 * every "brand colour + white text" or "brand colour on a white card"
 * assumption fails the day someone's brand is white or pastel yellow.
 */

import { DEFAULT_BRANDING } from "@/types/ui/branding";

type Rgb = [number, number, number];

const DARK_INK = "#111827";
const DEEPEN_FALLBACK = "#222B45";

/** Luminance at or below which white text reads at WCAG AA (4.5:1). */
const WHITE_TEXT_MAX_LUMINANCE = 0.18;

/**
 * Luminance above which a colour stops reading against a white surface
 * (about 2.6:1). The house #4E9CFF and #FF4DB8 both clear it, so only brands
 * lighter than the platform's own palette are ever adjusted.
 */
const ON_WHITE_MAX_LUMINANCE = 0.35;

/** Reads #rgb, #rgba, #rrggbb and #rrggbbaa (alpha dropped); null for anything else. */
export const parseHex = (hex: string): Rgb | null => {
  const value = hex.trim().replace(/^#/, "");
  if (!/^([0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(value)) return null;
  const expanded = value.length <= 4
    ? value.split("").map((c) => c + c).join("")
    : value;
  return [
    parseInt(expanded.slice(0, 2), 16),
    parseInt(expanded.slice(2, 4), 16),
    parseInt(expanded.slice(4, 6), 16),
  ];
};

const toHex = (rgb: Rgb): string =>
  `#${rgb.map((v) => Math.round(v).toString(16).padStart(2, "0")).join("")}`;

/** WCAG relative luminance, 0 (black) to 1 (white). */
export const luminance = (rgb: Rgb): number => {
  const [r, g, b] = rgb.map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/**
 * The text/icon colour to use on top of a brand colour: white on anything
 * mid-tone or darker, near-black on light brands. The 0.45 cut keeps white ink
 * on the platform default #4E9CFF while flipping pastels and yellows to dark.
 */
export const inkOn = (brandColor: string): string => {
  const rgb = parseHex(brandColor);
  if (!rgb) return "#FFFFFF";
  return luminance(rgb) > 0.45 ? DARK_INK : "#FFFFFF";
};

/**
 * The brand colour itself when white text already reads on it, otherwise the
 * same hue darkened just enough that it does. Dark and mid-tone brands come back
 * unchanged; pastels and yellows come back as a deep shade of themselves, never
 * swapped for a Help Genie colour.
 */
export const deepen = (brandColor: string): string => {
  const rgb = parseHex(brandColor);
  if (!rgb) return DEEPEN_FALLBACK;
  if (luminance(rgb) <= WHITE_TEXT_MAX_LUMINANCE) return toHex(rgb);
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 16; i += 1) {
    const mid = (lo + hi) / 2;
    const shaded = rgb.map((v) => v * mid) as Rgb;
    if (luminance(shaded) <= WHITE_TEXT_MAX_LUMINANCE) lo = mid;
    else hi = mid;
  }
  return toHex(rgb.map((v) => v * lo) as Rgb);
};

/**
 * The brand colour for use on the app's white cards — as text, an icon, a
 * border, or a fill that carries white text. Colours that already stand off
 * white come back untouched; a white or pastel brand comes back deepened so it
 * can never disappear into the surface or swallow the label sitting on it.
 *
 * Always returns a 6-digit hex, because these surfaces append alpha digits to
 * it. A stored value that is not a hex colour at all would paint nothing, so it
 * resolves to `fallback` instead.
 */
export const readableOnWhite = (
  brandColor: string,
  fallback: string = DEFAULT_BRANDING.primaryColor,
): string => {
  const rgb = parseHex(brandColor);
  if (!rgb) return fallback;
  return luminance(rgb) <= ON_WHITE_MAX_LUMINANCE ? toHex(rgb) : deepen(brandColor);
};
